/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { GripVertical } from 'lucide-react';
import { cn } from '../../../../lib/utils';

const MIN_WIDTH = 280;
const DEFAULT_WIDTH = 320;
const STORAGE_KEY = 'grow-skill-tree-width';

function ResizablePanel({ children, className }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [maxWidth, setMaxWidth] = useState(600);

  // Calculate max width (50% of container)
  useEffect(() => {
    const updateMaxWidth = () => {
      if (containerRef.current?.parentElement) {
        const parentWidth = containerRef.current.parentElement.offsetWidth;
        setMaxWidth(Math.floor(parentWidth * 0.5));
      }
    };

    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  // Persist width to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, width.toString());
  }, [width]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;

      // Clamp width between min and max
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(newWidth, maxWidth));
      setWidth(clampedWidth);
    },
    [isResizing, maxWidth],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove global mouse listeners when resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={containerRef}
      className={cn('relative shrink-0 flex', className)}
      style={{ width: `${width}px` }}
    >
      {/* Content */}
      <div className="flex-1 min-w-0">{children}</div>

      {/* Resize Handle */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={cn(
          'absolute right-0 top-0 bottom-0 w-4 -mr-2 z-10',
          'flex items-center justify-center',
          'cursor-col-resize group',
          'hover:bg-primary/5 transition-colors',
          isResizing && 'bg-primary/10',
        )}
        onMouseDown={handleMouseDown}
      >
        <GripVertical
          className={cn(
            'absolute h-4 w-4 text-muted-foreground/50',
            'group-hover:text-primary/70 transition-colors',
            isResizing && 'text-primary',
          )}
        />
      </div>
    </div>
  );
}

ResizablePanel.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

ResizablePanel.defaultProps = {
  className: '',
};

export default ResizablePanel;
